import { useState, useEffect } from "react";
import { 
  Search, MapPin, Phone, Building2, User, FileCheck, Filter,
  ChevronLeft, ChevronRight, Download, Send, CheckCircle2, AlertCircle,
  Loader2, ExternalLink, Users, Mail, RefreshCw, Zap, Shield, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// State options
const SUPPORTED_STATES = [
  { code: "WA", name: "Washington", recommended: true, hasAddress: true, hasPhone: true },
  { code: "OR", name: "Oregon", recommended: true, hasAddress: true, hasPhone: true, hasEmail: true },
  { code: "TX", name: "Texas", recommended: false, hasAddress: false, hasPhone: false, hasEnrichment: true }
];

// License type options
const LICENSE_TYPES = [
  { value: "all", label: "All Types" },
  { value: "general", label: "General Contractor" },
  { value: "electrical", label: "Electrical" },
  { value: "hvac", label: "HVAC" },
  { value: "plumbing", label: "Plumbing" }
];

// Status options
const STATUS_OPTIONS = [
  { value: "Active", label: "Active Only" },
  { value: "all", label: "All Statuses" },
  { value: "Expired", label: "Expired" }
];

export default function ContractorRecruitmentPage() {
  // Search state
  const [searchParams, setSearchParams] = useState({
    state: "WA",
    city: "",
    zip_code: "",
    license_type: "all",
    license_status: "Active",
    limit: 25,
    offset: 0
  });
  
  // Results state
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [source, setSource] = useState("");
  const [hasAddressData, setHasAddressData] = useState(true);
  const [fortressFilterActive, setFortressFilterActive] = useState(false);
  const [enrichedCount, setEnrichedCount] = useState(0);
  
  // Selection state
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Invite state
  const [inviting, setInviting] = useState(false);
  
  // Enrichment state (for Texas Contact Command)
  const [enriching, setEnriching] = useState(null); // license_number being enriched

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recruitment/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  // Texas Contact Command - Enrich single contact
  const handleEnrichContact = async (contractor) => {
    setEnriching(contractor.license_number);
    
    try {
      const response = await fetch(`${API_URL}/api/recruitment/texas/enrich-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          license_number: contractor.license_number,
          business_name: contractor.business_name
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update the result in the list
        setResults(prev => prev.map(r => 
          r.license_number === contractor.license_number
            ? { 
                ...r, 
                phone: data.phone || r.phone,
                email: data.email || r.email,
                address: data.address || r.address,
                city: data.city || r.city,
                zip_code: data.zip_code || r.zip_code,
                enrichment_status: data.status,
                confidence_score: data.confidence_score
              }
            : r
        ));
        
        if (data.phone || data.email) {
          toast.success(`Found contact info for ${contractor.business_name}!`);
        } else {
          toast.info(`No contact info found for ${contractor.business_name}`);
        }
      } else {
        toast.error("Failed to enrich contact");
      }
    } catch (error) {
      toast.error("Enrichment failed: " + error.message);
    } finally {
      setEnriching(null);
    }
  };

  // Texas Contact Command - Bulk enrich selected contacts
  const handleBulkEnrich = async () => {
    if (selectedContractors.length === 0) {
      toast.error("Please select contractors to enrich");
      return;
    }
    
    const toEnrich = results.filter(r => 
      selectedContractors.includes(r.license_number) && 
      r.enrichment_status !== "enriched"
    );
    
    if (toEnrich.length === 0) {
      toast.info("All selected contractors are already enriched");
      return;
    }
    
    setEnriching("bulk");
    toast.info(`Enriching ${toEnrich.length} contacts...`);
    
    try {
      const response = await fetch(`${API_URL}/api/recruitment/texas/enrich-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractors: toEnrich,
          max_items: 50
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update results with enriched data
        const enrichedMap = {};
        data.results.forEach(r => {
          enrichedMap[r.license_number] = r;
        });
        
        setResults(prev => prev.map(r => {
          const enriched = enrichedMap[r.license_number];
          if (enriched) {
            return {
              ...r,
              phone: enriched.phone || r.phone,
              email: enriched.email || r.email,
              enrichment_status: enriched.status,
              confidence_score: enriched.confidence_score
            };
          }
          return r;
        }));
        
        toast.success(`Enriched ${data.newly_enriched} contacts. ${data.from_cache} from cache.`);
      }
    } catch (error) {
      toast.error("Bulk enrichment failed");
    } finally {
      setEnriching(null);
    }
  };

  const handleSearch = async (newOffset = 0) => {
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/recruitment/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...searchParams,
          offset: newOffset
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Search failed");
      }
      
      const data = await response.json();
      setResults(data.results || []);
      setTotalCount(data.total_count || 0);
      setSource(data.source || "");
      setHasAddressData(data.has_address_data !== false);
      setFortressFilterActive(data.fortress_filter_active || false);
      setEnrichedCount(data.enriched_count || 0);
      setSearchParams(prev => ({ ...prev, offset: newOffset }));
      setSelectedContractors([]);
      setSelectAll(false);
      
      // Show special message for Texas with Fortress Filter
      if (data.fortress_filter_active) {
        toast.success("🏰 FORTRESS FILTER active: Showing only medical-ops relevant licenses");
      } else if (data.note) {
        toast.info(data.note);
      }
    } catch (error) {
      toast.error(error.message);
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContractors([]);
    } else {
      setSelectedContractors(results.map(r => r.license_number));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectContractor = (licenseNumber) => {
    setSelectedContractors(prev => {
      if (prev.includes(licenseNumber)) {
        return prev.filter(l => l !== licenseNumber);
      }
      return [...prev, licenseNumber];
    });
  };

  const handleInvite = async () => {
    if (selectedContractors.length === 0) {
      toast.error("Please select at least one contractor to invite");
      return;
    }
    
    setInviting(true);
    
    try {
      const contractorsToInvite = results.filter(r => 
        selectedContractors.includes(r.license_number)
      );
      
      const response = await fetch(`${API_URL}/api/recruitment/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractors: contractorsToInvite
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to send invitations");
      }
      
      const data = await response.json();
      
      // Show detailed success message
      if (data.emails_sent > 0) {
        toast.success(`✉️ ${data.emails_sent} emails sent! ${data.invited_count} contractors invited. ${data.skipped_count} already in system.`);
      } else {
        toast.success(`📋 ${data.invited_count} contractors added to invite list. ${data.skipped_count} already invited.`);
      }
      
      setSelectedContractors([]);
      setSelectAll(false);
      loadStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInviting(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "—";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const totalPages = Math.ceil(totalCount / searchParams.limit);
  const currentPage = Math.floor(searchParams.offset / searchParams.limit) + 1;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Contractor Recruitment</h1>
          <p className="text-slate-600">
            Search public licensing databases to find and invite contractors to join the AskIsaac network.
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_invites}</p>
                  <p className="text-sm text-slate-500">Total Invites</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_status?.pending || 0}</p>
                  <p className="text-sm text-slate-500">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_status?.sent || 0}</p>
                  <p className="text-sm text-slate-500">Sent</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_status?.joined || 0}</p>
                  <p className="text-sm text-slate-500">Joined</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Form */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-900">Search Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {/* State */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">State *</label>
              <select
                value={searchParams.state}
                onChange={(e) => setSearchParams({ ...searchParams, state: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {SUPPORTED_STATES.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name} {state.recommended ? "(Recommended)" : ""}
                  </option>
                ))}
              </select>
            </div>
            
            {/* City */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">City</label>
              <Input
                placeholder="e.g., Seattle"
                value={searchParams.city}
                onChange={(e) => setSearchParams({ ...searchParams, city: e.target.value })}
              />
            </div>
            
            {/* ZIP Code */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">ZIP Code</label>
              <Input
                placeholder="e.g., 98101"
                value={searchParams.zip_code}
                onChange={(e) => setSearchParams({ ...searchParams, zip_code: e.target.value })}
              />
            </div>
            
            {/* License Type */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Trade Type</label>
              <select
                value={searchParams.license_type}
                onChange={(e) => setSearchParams({ ...searchParams, license_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {LICENSE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            {/* Status */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">License Status</label>
              <select
                value={searchParams.license_status}
                onChange={(e) => setSearchParams({ ...searchParams, license_status: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {STATUS_OPTIONS.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Data sourced from public state licensing board records
            </p>
            <Button 
              onClick={() => handleSearch(0)}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Search Contractors
            </Button>
          </div>
        </div>

        {/* Texas Contact Command Banner */}
        {searched && searchParams.state === "TX" && fortressFilterActive && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold flex items-center gap-2">
                    🏰 FORTRESS FILTER Active
                  </p>
                  <p className="text-amber-100 text-sm">Only showing medical-ops relevant licenses (HVAC, Electrical, Plumbing, Elevator, Boiler)</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-amber-100">Contact Discovery Engine</p>
                <p className="font-bold">{enrichedCount} contacts enriched</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {searched && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Results Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">
                  {totalCount.toLocaleString()} contractors found
                </p>
                {source && <p className="text-sm text-slate-500">Source: {source}</p>}
              </div>
              
              <div className="flex items-center gap-3">
                {/* Bulk Enrich Button for Texas */}
                {searchParams.state === "TX" && selectedContractors.length > 0 && (
                  <Button 
                    onClick={handleBulkEnrich}
                    disabled={enriching === "bulk"}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    {enriching === "bulk" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Find Contacts ({selectedContractors.length})
                  </Button>
                )}
                
                {selectedContractors.length > 0 && (
                  <Button 
                    onClick={handleInvite}
                    disabled={inviting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {inviting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Invite Selected ({selectedContractors.length})
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleSearch(searchParams.offset)}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Results Table */}
            {results.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Business Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Owner</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Specialty</th>
                      {hasAddressData && (
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Location</th>
                      )}
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {results.map((contractor, index) => (
                      <tr 
                        key={`${contractor.license_number}-${index}`}
                        className={`hover:bg-slate-50 transition-colors ${
                          selectedContractors.includes(contractor.license_number) ? "bg-blue-50" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedContractors.includes(contractor.license_number)}
                            onChange={() => handleSelectContractor(contractor.license_number)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{contractor.business_name}</p>
                              <p className="text-xs text-slate-500">#{contractor.license_number}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-700">{contractor.owner_name || "—"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                            {contractor.specialty || contractor.license_type || "—"}
                          </span>
                        </td>
                        {hasAddressData && (
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                              <div className="text-sm text-slate-700">
                                <p>{contractor.city}, {contractor.state} {contractor.zip_code}</p>
                                {contractor.address && (
                                  <p className="text-xs text-slate-500">{contractor.address}</p>
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-3">
                          {contractor.phone ? (
                            <div className="flex flex-col gap-1">
                              <a 
                                href={`tel:${contractor.phone}`}
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                              >
                                <Phone className="w-4 h-4" />
                                {formatPhone(contractor.phone)}
                              </a>
                              {contractor.confidence_score && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  contractor.confidence_score === "high" ? "bg-emerald-100 text-emerald-700" :
                                  contractor.confidence_score === "medium" ? "bg-amber-100 text-amber-700" :
                                  "bg-slate-100 text-slate-600"
                                }`}>
                                  {contractor.confidence_score} confidence
                                </span>
                              )}
                            </div>
                          ) : searchParams.state === "TX" ? (
                            <button
                              onClick={() => handleEnrichContact(contractor)}
                              disabled={enriching === contractor.license_number}
                              className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                contractor.enrichment_status === "no_contact_found"
                                  ? "bg-slate-100 text-slate-500 cursor-not-allowed"
                                  : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                              }`}
                              title={contractor.enrichment_status === "no_contact_found" ? "No contact found" : "Find contact info via web search"}
                            >
                              {enriching === contractor.license_number ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Searching...
                                </>
                              ) : contractor.enrichment_status === "no_contact_found" ? (
                                <>
                                  <AlertCircle className="w-3 h-3" />
                                  Not found
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3" />
                                  Find Contact
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            contractor.license_status === "ACTIVE" || contractor.license_status === "Active"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}>
                            {contractor.license_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700">
                            {contractor.expiration_date || "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No contractors found matching your criteria.</p>
                <p className="text-sm text-slate-400 mt-1">Try adjusting your search filters.</p>
              </div>
            )}

            {/* Pagination */}
            {totalCount > searchParams.limit && (
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Showing {searchParams.offset + 1}-{Math.min(searchParams.offset + searchParams.limit, totalCount)} of {totalCount.toLocaleString()}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearch(Math.max(0, searchParams.offset - searchParams.limit))}
                    disabled={searchParams.offset === 0 || loading}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearch(searchParams.offset + searchParams.limit)}
                    disabled={searchParams.offset + searchParams.limit >= totalCount || loading}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial State */}
        {!searched && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Search Public Licensing Records</h3>
            <p className="text-slate-500 max-w-md mx-auto mb-6">
              Enter a city, ZIP code, or trade type above to search for licensed contractors in your area.
              Currently supporting Washington & Oregon (full data) and Texas (license data only).
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Public Records</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Real-time Data</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Bulk Invites</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
